import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import DataTable from 'react-data-table-component';
import { toast } from 'react-toastify';
import { formatCurrency, formatDate } from '../../../utils/formatters';
import { useGetPaymentTrackingCollectionSheet } from '../../../services/loanService';
import type { PaymentTrackingRecord, PaymentTrackingRecordStatus } from '../../../services/schemas/loanServiceSchemas';

const CollectionsOverview = () => {
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(50);
  const ALL_COLLECTIONS_PAGE_SIZE = 10000;
  const navigate = useNavigate();

  // Initialize date filters with current month (first to last day)
  const getCurrentMonthBounds = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    
    // Format dates as YYYY-MM-DD without timezone conversion
    const formatDate = (date: Date) => {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, '0');
      const d = String(date.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    };
    
    return {
      from: formatDate(firstDay),
      to: formatDate(lastDay)
    };
  };

  const currentMonthBounds = getCurrentMonthBounds();
  const [dateFrom, setDateFrom] = useState<string>(currentMonthBounds.from);
  const [dateTo, setDateTo] = useState<string>(currentMonthBounds.to);

  // Use the API hook - always enabled, will use current month dates on initial load
  const { data: collectionSheet, isLoading, error, refetch } = useGetPaymentTrackingCollectionSheet(
    { 
      page: 0, 
      size: ALL_COLLECTIONS_PAGE_SIZE,
      ...(dateFrom && { fromDate: dateFrom }),
      ...(dateTo && { toDate: dateTo })
    },
    { query: { enabled: true } }
  );

  const records = collectionSheet?.records?.content || [];
  const summary = collectionSheet?.summary;

  // Handle errors
  useEffect(() => {
    if (error) {
      console.error('Error fetching collection sheet:', error);
      toast.error('Failed to fetch collection sheet data');
    }
  }, [error]);


  // Get status badge color
  const getStatusColor = (status: PaymentTrackingRecordStatus | undefined) => {
    switch (status) {
      case 'Paid':
        return 'success';
      case 'Partial':
        return 'warning';
      case 'Overdue':
        return 'danger';
      case 'Current':
        return 'info';
      default:
        return 'secondary';
    }
  };

  // Handle page change
  const handlePageChange = (page: number) => {
    setPage(page);
  };

  // Handle rows per page change
  const handlePerRowsChange = (newPerPage: number) => {
    setSize(newPerPage);
    setPage(1); // Reset to first page
  };

  // Define table columns - only important ones to avoid horizontal scrolling
  const columns = [
    {
      name: 'Reference',
      selector: (row: PaymentTrackingRecord) => row.loanReferenceNo || '',
      sortable: true,
      cell: (row: PaymentTrackingRecord) => (
        <span className="badge bg-light text-dark border px-2 py-1">
          {row.loanReferenceNo || 'N/A'}
        </span>
      ),
      minWidth: '140px',
      maxWidth: '160px',
    },
    {
      name: 'Customer Name',
      selector: (row: PaymentTrackingRecord) => row.customerName || '',
      sortable: true,
      cell: (row: PaymentTrackingRecord) => (
        <div className="fw-semibold" style={{ wordBreak: 'break-word' }}>
          {row.customerName || 'N/A'}
        </div>
      ),
      minWidth: '200px',
      wrap: true,
    },
    {
      name: 'Expected',
      selector: (row: PaymentTrackingRecord) => row.expectedAmount || 0,
      sortable: true,
      cell: (row: PaymentTrackingRecord) => (
        <div className="text-end fw-semibold text-primary">
          {formatCurrency(row.expectedAmount || 0)}
        </div>
      ),
      minWidth: '120px',
      maxWidth: '140px',
    },
    {
      name: 'Collected',
      selector: (row: PaymentTrackingRecord) => row.amountCollected || 0,
      sortable: true,
      cell: (row: PaymentTrackingRecord) => (
        <div className="text-end fw-bold text-success">
          {formatCurrency(row.amountCollected || 0)}
        </div>
      ),
      minWidth: '120px',
      maxWidth: '140px',
    },
    {
      name: 'Remaining',
      selector: (row: PaymentTrackingRecord) => row.remainingAmount || 0,
      sortable: true,
      cell: (row: PaymentTrackingRecord) => {
        const remaining = row.remainingAmount || 0;
        return (
          <div className={`text-end fw-bold ${remaining > 0 ? 'text-danger' : 'text-success'}`}>
            {formatCurrency(remaining)}
          </div>
        );
      },
      minWidth: '120px',
      maxWidth: '140px',
    },
    {
      name: 'Commencement Date',
      selector: (row: PaymentTrackingRecord) => row.commencementDate || '',
      sortable: true,
      cell: (row: PaymentTrackingRecord) => (
        <div className="d-flex align-items-center">
          <i className="fa fa-calendar-check-o text-info me-2" style={{ fontSize: '0.875rem' }}></i>
          <span>{row.commencementDate ? formatDate(row.commencementDate) : 'N/A'}</span>
        </div>
      ),
      minWidth: '150px',
      maxWidth: '180px',
    },
    {
      name: 'Due Date',
      selector: (row: PaymentTrackingRecord) => row.dueDate || '',
      sortable: true,
      cell: (row: PaymentTrackingRecord) => (
        <div className="d-flex align-items-center">
          <i className="fa fa-calendar-times-o text-warning me-2" style={{ fontSize: '0.875rem' }}></i>
          <span>{row.dueDate ? formatDate(row.dueDate) : 'N/A'}</span>
        </div>
      ),
      minWidth: '150px',
      maxWidth: '180px',
    },
    {
      name: 'Status',
      selector: (row: PaymentTrackingRecord) => row.status || 'Current',
      sortable: true,
      cell: (row: PaymentTrackingRecord) => (
        <span className={`badge bg-${getStatusColor(row.status)} px-3 py-2`}>
          {row.status || 'Current'}
        </span>
      ),
      minWidth: '100px',
      maxWidth: '130px',
    },
    {
      name: 'Actions',
      cell: (row: PaymentTrackingRecord) => (
        <div className="d-flex gap-1 justify-content-center">
          {row.loanAccountId && (
            <button
              className="btn btn-sm btn-primary rounded-pill px-3"
              onClick={() => navigate(`/loan-accounts/${row.loanAccountId}`)}
              title="View full account details including interest, dates, and actor"
            >
              <i className="fa fa-eye me-1"></i>
              <span style={{ fontSize: '0.85rem' }}>View</span>
            </button>
          )}
        </div>
      ),
      minWidth: '100px',
      maxWidth: '130px',
      allowOverflow: true,
    },
  ];

  return (
    <>
      {/* Header */}
      <div className="page-toolbar px-xl-4 px-sm-2 px-0 py-4 bg-primary-gradient">
        <div className="container-fluid">
          <div className="row g-3 mb-3 align-items-center">
            <div className="col">
              <ol className="breadcrumb bg-transparent mb-0">
                <li className="breadcrumb-item">
                  <Link className="text-white" to="/">
                    <i className="fa fa-home me-2"></i>Home
                  </Link>
                </li>
                <li className="breadcrumb-item">
                  <Link className="text-white" to="/loan-book">Loan Book</Link>
                </li>
                <li className="breadcrumb-item active text-white" aria-current="page">Collections Overview</li>
              </ol>
            </div>
          </div>
          <div className="row align-items-center">
            <div className="col-12">
              <h1 className="fs-2 fw-bold mb-2 text-white">
                Collections Overview 📊
              </h1>
              <p className="mb-0 text-white fs-6 opacity-75">
                Payment Tracking Collection Sheet
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="page-body px-xl-4 px-sm-2 px-0 py-lg-2 py-1 mt-0 mt-lg-3">
        <div className="container-fluid">
          {/* Summary Cards */}
          {summary && (
            <div className="row g-4 mb-4">
              <div className="col-lg-3 col-md-6">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-body p-4">
                    <div className="d-flex align-items-center justify-content-between">
                      <div>
                        <p className="text-muted small mb-1">Expected Amount</p>
                        <h3 className="mb-0 fw-bold text-primary">
                          {formatCurrency(summary.totalExpectedAmount || summary.monthlyExpectedAmount || 0)}
                        </h3>
                      </div>
                      <div className="rounded-circle p-3" style={{ backgroundColor: '#f8f9fa', border: '1px solid rgba(0,123,255,0.2)' }}>
                        <i className="fa fa-money fa-2x text-primary" style={{ display: 'block' }}></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-lg-3 col-md-6">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-body p-4">
                    <div className="d-flex align-items-center justify-content-between">
                      <div>
                        <p className="text-muted small mb-1">Interest Earned</p>
                        <h3 className="mb-0 fw-bold text-info">
                          {formatCurrency(summary.totalInterestEarned || summary.monthlyInterestAmount || 0)}
                        </h3>
                      </div>
                      <div className="rounded-circle p-3" style={{ backgroundColor: '#f8f9fa', border: '1px solid rgba(23,162,184,0.2)' }}>
                        <i className="fa fa-level-up fa-2x text-info" style={{ display: 'block' }}></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-lg-3 col-md-6">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-body p-4">
                    <div className="d-flex align-items-center justify-content-between">
                      <div>
                        <p className="text-muted small mb-1">Amount Collected</p>
                        <h3 className="mb-0 fw-bold text-success">
                          {formatCurrency(summary.totalCollectedAmount || summary.monthlyAmountCollected || 0)}
                        </h3>
                      </div>
                      <div className="rounded-circle p-3" style={{ backgroundColor: '#f8f9fa', border: '1px solid rgba(40,167,69,0.2)' }}>
                        <i className="fa fa-check-circle fa-2x text-success" style={{ display: 'block' }}></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-lg-3 col-md-6">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-body p-4">
                    <div className="d-flex align-items-center justify-content-between">
                      <div>
                        <p className="text-muted small mb-1">Remaining</p>
                        <h3 className="mb-0 fw-bold text-danger">
                          {formatCurrency(summary.totalRemainingAmount || summary.monthlyRemainingDue || 0)}
                        </h3>
                      </div>
                      <div className="rounded-circle p-3" style={{ backgroundColor: '#f8f9fa', border: '1px solid rgba(220,53,69,0.2)' }}>
                        <i className="fa fa-exclamation-triangle fa-2x text-danger" style={{ display: 'block' }}></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Summary Statistics Row */}
          {summary && (
            <div className="row g-3 mb-4">
              <div className="col-md-3">
                <div className="card border-0 shadow-sm">
                  <div className="card-body p-3">
                    <div className="text-center">
                      <p className="text-muted small mb-1">Percentage of Payments</p>
                      <h4 className="mb-0 fw-bold text-primary">
                        {(summary.percentageOfPayments || 0).toFixed(2)}%
                      </h4>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card border-0 shadow-sm">
                  <div className="card-body p-3">
                    <div className="text-center">
                      <p className="text-muted small mb-1">Number of Payers</p>
                      <h4 className="mb-0 fw-bold text-success">
                        {summary.numberOfPayers || 0}
                      </h4>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card border-0 shadow-sm">
                  <div className="card-body p-3">
                    <div className="text-center">
                      <p className="text-muted small mb-1">Total Number of Payers</p>
                      <h4 className="mb-0 fw-bold text-info">
                        {summary.totalNumberOfPayers || 0}
                      </h4>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card border-0 shadow-sm">
                  <div className="card-body p-3">
                    <div className="text-center">
                      <p className="text-muted small mb-1">Percentage of Payers</p>
                      <h4 className="mb-0 fw-bold text-warning">
                        {(summary.percentageOfPayers || 0).toFixed(2)}%
                      </h4>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Date Filter Section */}
          <div className="row mb-4">
            <div className="col-12">
              <div className="card border-0 shadow-sm" style={{ background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)' }}>
                <div className="card-header bg-transparent border-0 pb-2">
                  <h5 className="mb-0 fw-bold d-flex align-items-center">
                    <i className="fa fa-filter text-primary me-2"></i>
                    Date Range Filter
                  </h5>
                </div>
                <div className="card-body p-4">
                  <div className="row g-3">
                    <div className="col-md-5">
                      <label htmlFor="dateFrom" className="form-label fw-semibold mb-2 text-dark">
                        <i className="fa fa-calendar-check-o me-2 text-primary"></i>
                        From Date
                      </label>
                      <input
                        type="date"
                        id="dateFrom"
                        className="form-control form-control-lg"
                        value={dateFrom}
                        max={dateTo || undefined}
                        onChange={(e) => {
                          const selectedDate = e.target.value;
                          setDateFrom(selectedDate);
                          // If dateTo is before the new dateFrom, reset dateTo
                          if (dateTo && selectedDate && dateTo < selectedDate) {
                            setDateTo('');
                          }
                          setPage(0);
                        }}
                        style={{ 
                          border: '2px solid #e9ecef',
                          borderRadius: '8px',
                          transition: 'all 0.3s ease'
                        }}
                        onFocus={(e) => {
                          e.target.style.borderColor = '#0d6efd';
                          e.target.style.boxShadow = '0 0 0 0.2rem rgba(13, 110, 253, 0.25)';
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = '#e9ecef';
                          e.target.style.boxShadow = 'none';
                        }}
                      />
                    </div>
                    <div className="col-md-5">
                      <label htmlFor="dateTo" className="form-label fw-semibold mb-2 text-dark">
                        <i className="fa fa-calendar-times-o me-2 text-primary"></i>
                        To Date
                      </label>
                      <input
                        type="date"
                        id="dateTo"
                        className="form-control form-control-lg"
                        value={dateTo}
                        min={dateFrom || undefined}
                        onChange={(e) => {
                          const selectedDate = e.target.value;
                          setDateTo(selectedDate);
                          // If dateFrom is after the new dateTo, reset dateFrom
                          if (dateFrom && selectedDate && dateFrom > selectedDate) {
                            setDateFrom('');
                          }
                          setPage(0);
                        }}
                        style={{ 
                          border: '2px solid #e9ecef',
                          borderRadius: '8px',
                          transition: 'all 0.3s ease'
                        }}
                        onFocus={(e) => {
                          e.target.style.borderColor = '#0d6efd';
                          e.target.style.boxShadow = '0 0 0 0.2rem rgba(13, 110, 253, 0.25)';
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = '#e9ecef';
                          e.target.style.boxShadow = 'none';
                        }}
                      />
                    </div>
                    <div className="col-md-2 d-flex align-items-end">
                      <button
                        className="btn btn-outline-danger w-100"
                        onClick={() => {
                          const bounds = getCurrentMonthBounds();
                          setDateFrom(bounds.from);
                          setDateTo(bounds.to);
                          setPage(0);
                        }}
                        title="Reset to current month"
                        style={{ 
                          height: '48px',
                          borderRadius: '8px',
                          fontWeight: '600'
                        }}
                      >
                        <i className="fa fa-refresh me-2"></i>
                        Reset
                      </button>
                    </div>
                  </div>
                  <div className="row mt-3">
                    <div className="col-12">
                      <div className="alert alert-info mb-0 py-2" style={{ borderRadius: '6px', fontSize: '0.875rem' }}>
                        <i className="fa fa-info-circle me-2"></i>
                        <strong>Tip:</strong> Select a date range to filter payment tracking records. The "To Date" must be after the "From Date".
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Payment Tracking Table */}
          <div className="row">
            <div className="col-12">
              <div className="card border-0 shadow-sm">
                <div className="card-header bg-white border-0 py-3 px-4">
                  <div className="d-flex justify-content-between align-items-center">
                    <h5 className="mb-0 fw-bold d-flex align-items-center">
                      <i className="fa fa-list text-primary me-2"></i>
                      Payment Tracking Records
                    </h5>
                  </div>
                </div>
                <div className="card-body p-0">
                  {isLoading ? (
                    <div className="text-center py-5">
                      <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <p className="mt-3 text-muted">Loading collection sheet...</p>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <DataTable
                        columns={columns}
                        data={records}
                        pagination
                        onChangePage={handlePageChange}
                        onChangeRowsPerPage={handlePerRowsChange}
                        paginationPerPage={size}
                        paginationRowsPerPageOptions={[10, 25, 50, 100]}
                        highlightOnHover
                        striped
                        fixedHeader
                        fixedHeaderScrollHeight="calc(100vh - 450px)"
                        progressPending={isLoading}
                        noDataComponent={
                          <div className="text-center py-5">
                            <i className="fa fa-search fa-3x text-muted mb-3"></i>
                            <h5 className="text-muted">No payment records found</h5>
                            <p className="text-muted">Try adjusting your date filters</p>
                          </div>
                        }
                        paginationComponentOptions={{
                          rowsPerPageText: 'Rows per page:',
                          rangeSeparatorText: 'of',
                          selectAllRowsItem: true,
                          selectAllRowsItemText: 'All',
                        }}
                        customStyles={{
                          table: {
                            style: {
                              backgroundColor: 'white',
                            },
                          },
                          headRow: {
                            style: {
                              backgroundColor: '#f8f9fa',
                              borderBottom: '2px solid #dee2e6',
                              fontWeight: 700,
                              fontSize: '0.8rem',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px',
                            },
                          },
                          headCells: {
                            style: {
                              paddingLeft: '1rem',
                              paddingRight: '1rem',
                              paddingTop: '1rem',
                              paddingBottom: '1rem',
                              whiteSpace: 'nowrap',
                              fontSize: '0.8rem',
                            },
                          },
                          cells: {
                            style: {
                              paddingLeft: '1rem',
                              paddingRight: '1rem',
                              paddingTop: '0.75rem',
                              paddingBottom: '0.75rem',
                              fontSize: '0.85rem',
                            },
                          },
                          rows: {
                            style: {
                              minHeight: '55px',
                              borderBottom: '1px solid #f8f9fa',
                            },
                            stripedStyle: {
                              backgroundColor: '#ffffff',
                            },
                          },
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CollectionsOverview;

